import { useState, useEffect } from 'react';
import { X, ChevronRight, Bot, Zap } from 'lucide-react';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { useGetBusinessAgentsQuery } from '@/services/agentApi';
import { Spinner } from '@/components/ui/spinner';

export function AgentsNotification() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasBeenShown, setHasBeenShown] = useState<boolean>(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Get the business ID from your auth store
  const businessId = localStorage.getItem('businessId') || '';
  
  // Use the API query instead of static data
  const { 
    data: agentsData, 
    isLoading,
    isError
  } = useGetBusinessAgentsQuery(businessId, {
    skip: !isOpen // Only run the query when the notification is open
  });

  useEffect(() => {
    const hasShownBefore = localStorage.getItem('ctoNotificationShown');
    
    // Only show after a delay, and only if it hasn't been shown before in this session
    if (!hasShownBefore && !hasBeenShown) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasBeenShown(true);
        localStorage.setItem('ctoNotificationShown', 'true');
      }, 3000); // Show after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [hasBeenShown]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleNavigateToAgents = () => {
    navigate('/agents/list');
    handleClose();
    toast({
      title: "AI Agents Ready",
      description: "Configure your AI agents to automate your business processes.",
    });
  };

  // Get the available agents from the API response
  const availableAgents = agentsData?.availableAgents?.map((agentType: string) => {
    return {
      type: agentType,
      name: getAgentTitle(agentType),
      description: getAgentDescription(agentType)
    };
  }) || [];

  
  // Merge both lists for display
  const allAgents = [...availableAgents];

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        variant="default"
        size="sm"
        className="fixed right-0 top-2/3 transform -translate-y-1/2 rounded-l-md rounded-r-none shadow-lg bg-primary hover:bg-primary/90 z-40"
      >
        <Bot className="h-5 w-5 mr-2" />
        <span className="text-xs font-medium">AI Agents</span>
      </Button>
    );
  }

  return (
    <div className="fixed right-0 top-0 bottom-0 w-80 md:w-96 bg-card border-l z-50 shadow-xl flex flex-col transition-all duration-300 ease-in-out">
      <CardHeader className="bg-primary/5 border-b px-4 py-3 flex flex-row justify-between items-center space-y-0">
        <CardTitle className="text-base font-semibold flex items-center">
          <Bot className="mr-2 h-5 w-5" />
          AI Business Agents
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-auto p-4 space-y-4">
        <div className="space-y-2">
          <h3 className="text-sm font-medium flex items-center">
            <Zap className="h-4 w-4 mr-1 text-amber-500" />
            Automate Your Business Processes
          </h3>
          <p className="text-sm text-muted-foreground">
            Your business has AI agents available to configure. These intelligent agents can automate tasks, monitor compliance, generate reports, and more.
          </p>
        </div>
        
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Available Agents:</h4>
          
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              <p>There was a problem loading your agents.</p>
              <p className="mt-2">Please try again later.</p>
            </div>
          ) : allAgents.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              <p>No agents are available for your business at this time.</p>
              <p className="mt-2">Please contact support for more information.</p>
            </div>
          ) : (
            allAgents.map((agent) => (
              <div key={agent.type} className="border rounded-md p-3 bg-accent/20">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-sm">{agent.name}</div>
                    <p className="text-xs text-muted-foreground mt-1">{agent.description}</p>
                  </div>
                  {'isConfigured' in agent ? (
                    <Badge variant={agent.isEnabled ? "success" : "outline"} className="text-xs">
                      {agent.isEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-primary/10 text-primary text-xs">
                      Available
                    </Badge>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="bg-muted/50 rounded-md p-3 text-xs space-y-2 mt-2">
          <p className="font-medium">Benefits:</p>
          <ul className="space-y-1 pl-4 list-disc">
            <li>Reduce manual workload by up to 40%</li>
            <li>Ensure consistent quality and compliance</li>
            <li>Scale operations without adding headcount</li>
            <li>Make data-driven decisions with automated reporting</li>
          </ul>
        </div>
      </CardContent>
      
      <div className="border-t p-4">
        <Button 
          className="w-full" 
          onClick={handleNavigateToAgents}
        >
          Configure AI Agents
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
        <p className="text-xs text-center text-muted-foreground mt-3">
          Need help setting up your agents? Contact support.
        </p>
      </div>
    </div>
  );
}

// Helper functions to display agent names and descriptions
function getAgentTitle(agentType: string) {
  const titles = {
    'auto-assignment': 'Auto Assignment',
    'compliance-monitoring': 'Compliance Monitor',
    'report-generation': 'Report Generator',
    'client-communication': 'Client Communicator',
    'resource-request': 'Resource Manager',
    'shift-optimization': 'Shift Optimizer',
    'task-generation': 'Task Generator',
    'task-prioritization': 'Task Prioritizer'
  }
  return titles[agentType as keyof typeof titles] || agentType
}

function getAgentDescription(agentType: string) {
  const descriptions = {
    'auto-assignment': 'Automatically assigns tasks to team members based on skills and workload',
    'compliance-monitoring': 'Monitors projects for compliance issues and regulatory requirements',
    'report-generation': 'Creates customized reports on project status and performance',
    'client-communication': 'Automates client communications and updates',
    'resource-request': 'Manages resource allocation and order requests',
    'shift-optimization': 'Optimizes staff schedules for maximum efficiency',
    'task-generation': 'Creates recurring tasks based on project requirements and schedules',
    'task-prioritization': 'Automatically prioritizes tasks based on deadlines and impact'
  }
  return descriptions[agentType as keyof typeof descriptions] || 'AI agent for business automation'
}